import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from './Icon';
import useClickOutside from '../../hooks/useClickOutside';
import './NumericRangeFilter.css';

const formatPreview = (value, formatter) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof formatter === 'function') return formatter(value);
  return String(value);
};

const buildDisplayText = (label, min, max, formatter) => {
  const hasMin = min !== null && min !== undefined && min !== '';
  const hasMax = max !== null && max !== undefined && max !== '';

  if (!hasMin && !hasMax) return label;
  if (hasMin && hasMax) return `${formatPreview(min, formatter)} - ${formatPreview(max, formatter)}`;
  if (hasMin) return `>= ${formatPreview(min, formatter)}`;
  return `<= ${formatPreview(max, formatter)}`;
};

const parseInput = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) return null;
  return parsed;
};

const NumericRangeFilter = ({
  label,
  min = null,
  max = null,
  placeholderMin = '',
  placeholderMax = '',
  step = 1,
  formatter,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftMin, setDraftMin] = useState(min ?? '');
  const [draftMax, setDraftMax] = useState(max ?? '');
  const dropdownRef = useRef(null);

  useEffect(() => {
    setDraftMin(min ?? '');
    setDraftMax(max ?? '');
  }, [min, max]);

  const handleCloseDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside(dropdownRef, handleCloseDropdown);

  const hasActiveFilter = (min !== null && min !== undefined) || (max !== null && max !== undefined);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleApply = () => {
    const parsedMin = parseInput(draftMin);
    const parsedMax = parseInput(draftMax);
    const shouldSwap = parsedMin !== null && parsedMax !== null && parsedMin > parsedMax;
    const finalMin = shouldSwap ? parsedMax : parsedMin;
    const finalMax = shouldSwap ? parsedMin : parsedMax;
    if (shouldSwap) {
      setDraftMin(String(finalMin));
      setDraftMax(String(finalMax));
    }
    onChange({ min: finalMin, max: finalMax });
    setIsOpen(false);
  };

  const handleReset = () => {
    setDraftMin('');
    setDraftMax('');
    onChange({ min: null, max: null });
    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const displayText = buildDisplayText(label, min, max, formatter);
  const triggerAriaLabel = hasActiveFilter
    ? `Filtre ${label} actif : ${displayText}`
    : `Ouvrir le filtre ${label}`;

  return (
    <div className="numeric-range-filter" ref={dropdownRef}>
      <button
        className={`numeric-range-filter-trigger ${hasActiveFilter ? 'has-selection' : ''}`}
        onClick={handleToggle}
        type="button"
        aria-label={triggerAriaLabel}
      >
        <Icon name="Filter" size={16} />
        <span className="filter-text">{displayText}</span>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} />
      </button>

      {isOpen && (
        <div className="numeric-range-filter-dropdown">
          <div className="numeric-range-filter-header">
            <span className="numeric-range-filter-title">{label}</span>
            {hasActiveFilter && (
              <button
                className="filter-action-btn secondary"
                onClick={handleReset}
                type="button"
              >
                <Icon name="X" size={14} />
                Effacer
              </button>
            )}
          </div>

          <div className="numeric-range-filter-body">
            <label className="numeric-range-field">
              <span className="numeric-range-field-label">Min</span>
              <input
                type="number"
                value={draftMin}
                step={step}
                placeholder={placeholderMin}
                onChange={(e) => setDraftMin(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </label>
            <label className="numeric-range-field">
              <span className="numeric-range-field-label">Max</span>
              <input
                type="number"
                value={draftMax}
                step={step}
                placeholder={placeholderMax}
                onChange={(e) => setDraftMax(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </label>
          </div>

          <div className="numeric-range-filter-footer">
            <button
              className="filter-action-btn primary"
              onClick={handleApply}
              type="button"
            >
              <Icon name="Check" size={14} />
              Appliquer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NumericRangeFilter;
